import { useRef } from 'react'

import BiConnectionsPanel from '@/components/connections/BiConnectionsPanel'
import type { BiConnection } from '@/api/connections'

/**
 * The "I will provide the connection" flow shared by every agent surface
 * (Ask-AI docks, the Auto Agent page): opens the SAME connection editor the
 * Analytics tab uses, straight on the blank new-connection form, and reports
 * the connection the user saved so the caller can answer the waiting agent.
 * The record itself is persisted in the project (Metadata_Model/connections),
 * so the agent — and every later exploration — can keep using it.
 */

/** Compose the chat answer sent back to the waiting agent once a connection
 *  has been defined. Names the id so the CLI agents (which read it as prose)
 *  and the orchestrator (which resolves the id against the registered
 *  connections) both bind to the exact source. */
export function connectionAnswerText(conn: BiConnection): string {
  const type = conn.DATABASE_TYPE ? `, type ${conn.DATABASE_TYPE}` : ''
  const schema = conn.SCHEMA ? `, schema ${conn.SCHEMA}` : ''
  return (
    `I have saved the database connection "${conn.CONNECTION_ID}"${type}${schema} ` +
    'in this project. Use this registered connection as the data source ' +
    `(connection id "${conn.CONNECTION_ID}") and continue.`
  )
}

/**
 * Canonical, machine-exact restatement of the connection id, appended to a
 * CLI agent's answer.
 *
 * The CLI docks have no structured channel back to the agent — the answer is
 * prose — so the id previously survived only inside a quoted sentence. An
 * agent that re-cases or "tidies" it binds the object to nothing and the work
 * silently falls back to the project's own database. This line is unambiguous
 * and copy-able; keep the exact spelling.
 */
export function connectionAnswerMarker(connectionId: string): string {
  return `[connection_id=${connectionId}]`
}

/** Append the canonical id marker to an answer when one is known. */
export function withConnectionMarker(answer: string, connectionId?: string): string {
  const id = (connectionId || '').trim()
  return id ? `${answer}\n\n${connectionAnswerMarker(id)}` : answer
}

export default function ConnectionDefineFlow({
  project, open, onClose, onDefined,
}: {
  project: string
  open: boolean
  /** Closed without saving anything. */
  onClose: () => void
  /** The user picked a connection — the one they just defined or an existing
   *  one — via the panel's explicit "Use this connection" button (or, as a
   *  fallback, by closing the panel after a save). */
  onDefined: (conn: BiConnection) => void
}) {
  const savedRef = useRef<BiConnection | null>(null)
  if (!open) return null
  return (
    <BiConnectionsPanel
      project={project}
      variant="modal"
      open={open}
      initialNew
      // Explicit hand-off: the user says "use this one" and the panel closes
      // with the connection reported back. Without it the only way to answer
      // the waiting agent was to DISMISS the panel after a save — invisible,
      // and impossible for a connection that already existed.
      onAttach={(conn) => { savedRef.current = null; onDefined(conn) }}
      attachLabel="Use this connection"
      onChanged={(saved) => { if (saved) savedRef.current = saved }}
      onClose={() => {
        const saved = savedRef.current
        savedRef.current = null
        if (saved) onDefined(saved)
        else onClose()
      }}
    />
  )
}
